import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import axios from "axios";
import Swal from "sweetalert2";
import "../App.css";
import { VscSend } from "react-icons/vsc";
import ChatOnTask from "../components/general/ChatOnTask";
import {
  Box,
  Stack,
  Text,
  Textarea,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Flex,
  Button,
  Skeleton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  Checkbox,
  ButtonGroup,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Card,
  CardBody,
  Badge,
  Icon,
  Tooltip,
  useToast,
  FormControl,
  FormLabel,
  FormHelperText,
  HStack,
  VStack,
  Spinner,
  useColorModeValue,
  IconButton,
} from "@chakra-ui/react";
import Datepicker from "../components/controls/DatePicker";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FiInfo, FiCalendar, FiUsers, FiShield, FiToggleLeft, FiToggleRight } from "react-icons/fi";

const UserDetail = ({ companyId, onCustomerUpdated, onClose }) => {
  const [company, setCompany] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const toast = useToast();
  const cancelRef = React.useRef();
  
  // Color mode values
  const bg = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenActive,
    onOpen: onOpenActive,
    onClose: onCloseActive,
  } = useDisclosure();
  
  const [formData, setFormData] = useState({
    company_expired: "",
    total_employee: "",
    verified: null, 
    status: "",
    reminders: {
      seven_days: true,
      one_day: false,
      same_day: false,
    }
  });

  const [originalFormData, setOriginalFormData] = useState({});

  const fetchCompany = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/companies/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const company = response.data.data;

      setCompany(company);

      const initialFormData = {
        company_expired: company.company_expired,
        total_employee: company.total_employee,
        verified: company.verified,
        status: company.status,
        reminders: {
          seven_days: true,
          one_day: false,
          same_day: false,
        }
      };

      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
      setIsLoading(false);
    } catch (error) {
      console.error(`Error fetching company details for ID ${companyId}:`, error);
      toast({
        title: "Error",
        description: "Failed to load company details",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, [companyId]);

  // Check for changes
  useEffect(() => {
    const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    setHasChanges(hasFormChanges);
  }, [formData, originalFormData]);

  const getVerifyText = (verified) => {
    switch (verified) {
      case true:
        return "Premium";
      case false:
        return "Free Trial";
      default:
        return "Unknown";
    }
  };

  const getVerifyColorScheme = (verified) => {
    switch (verified) {
      case true:
        return "yellow";
      case false:
        return "gray";
      default:
        return "gray";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Belum Ditentukan";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSwitchChange = (event, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: event.target.checked,
    });
  };

  const handleReminderChange = (event, reminderType) => {
    setFormData({
      ...formData,
      reminders: {
        ...formData.reminders,
        [reminderType]: event.target.checked,
      }
    });
  };

  const handleSwitchActiveChange = (event, fieldName) => {
    const isChecked = event.target.checked;
    setFormData({
      ...formData,
      [fieldName]: isChecked ? "active" : "inactive", 
    });
    onOpenActive();
  };

  const [selectedExpiredDate, setSelectedExpiredDate] = useState("");

  const handleExpiredDateChange = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day} 00:00:00`;

    setSelectedExpiredDate(formattedDate);
    setFormData({
      ...formData,
      company_expired: formattedDate,
    });
  };

  const [totalEmployees, setTotalEmployees] = useState("");

  useEffect(() => {
    if (company.total_employee) {
      setTotalEmployees(company.total_employee);
    }
  }, [company.total_employee]);

  const handleTotalEmployeesChange = (value) => {
    setTotalEmployees(value);
    setFormData({
      ...formData,
      total_employee: value,
    });
  };

  const handleUpdateCompany = async (e) => {
    if (e) e.preventDefault();
    setIsUpdating(true);

    const updatedData = {
      ...formData,
    };

    // Only send changed fields
    if (!updatedData.company_expired || updatedData.company_expired === company.company_expired) {
      updatedData.company_expired = company.company_expired;
    }
    if (!updatedData.total_employee || updatedData.total_employee === company.total_employee) {
      updatedData.total_employee = company.total_employee;
    }
    if (!updatedData.status || updatedData.status === company.status) {
      updatedData.status = company.status;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/update-company/${companyId}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast({
        title: "Success!",
        description: "Company updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setOriginalFormData({ ...formData });
      await fetchCompany();
      onCustomerUpdated && onCustomerUpdated();
      onCloseActive();
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    setIsUpdating(true);

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/companies/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 204) {
        toast({
          title: "Success",
          description: "Company deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onCustomerUpdated && onCustomerUpdated();
      onClose();
    } catch (error) {
      console.error(`Error deleting company with ID ${companyId}:`, error);
      toast({
        title: "Error",
        description: "Failed to delete company",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
      onCloseDelete();
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const handleCheckboxChange = () => {
    setConfirmDelete(!confirmDelete);
  };

  const handleResetForm = (reason) => {
    if (reason === "update") {
      setSelectedExpiredDate("");
      setTotalEmployees(company.total_employee);
      setFormData({ ...originalFormData });
    } else if (reason === "active") {
      setFormData({
        ...formData,
        status: company.status,
      });
      onCloseActive();
    }
    setConfirmDelete(false);
  };

  const getShimmerClass = (verified) => {
    return verified ? 'shimmer-gold' : 'shimmer-silver';
  };

  if (isLoading) {
    return (
      <Box justifyContent="center" p={6}>
        <VStack spacing={6}>
          <Skeleton height="60px" width="100%" />
          <Skeleton height="40px" width="80%" />
          <Skeleton height="200px" width="100%" />
          <Skeleton height="150px" width="100%" />
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg={bg} borderRadius="lg" shadow="sm" maxW="4xl" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header Section */}
        <Card bg={cardBg} variant="filled">
          <CardBody>
            <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "start", md: "center" }} gap={4}>
              <VStack align="start" spacing={2} flex={1}>
                <Text fontSize="2xl" fontWeight="bold" color="gray.700">
                  Update Pelanggan
                </Text>
                <Text fontSize="xl" fontWeight="semibold" color="gray.900">
                  {company.company_name}
                </Text>
                <Badge 
                  colorScheme={getVerifyColorScheme(company.verified)}
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                  className={getShimmerClass(company.verified)}
                >
                  <HStack spacing={1}>
                    <Icon as={FiShield} boxSize={3} />
                    <Text fontWeight="600">{getVerifyText(company.verified)}</Text>
                  </HStack>
                </Badge>
              </VStack>
              
              {/* Status Indicator */}
              <VStack align="end" spacing={2}>
                <Badge 
                  colorScheme={company.status === "active" ? "green" : "red"}
                  size="md"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {company.status === "active" ? "Active" : "Inactive"}
                </Badge>
                <Text fontSize="sm" color="gray.500">
                  Status: {company.status}
                </Text>
              </VStack>
            </Flex>
          </CardBody>
        </Card>

        {/* Main Form Section */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              
              {/* Premium Toggle */}
              <FormControl>
                <Flex justify="space-between" align="center">
                  <VStack align="start" spacing={1} flex={1}>
                    <FormLabel mb={0} fontSize="lg" fontWeight="semibold" display="flex" alignItems="center" gap={2}>
                      <Icon as={FiShield} color="blue.400" />
                      Premium Status
                    </FormLabel>
                    <FormHelperText mt={0} fontSize="sm">
                      Aktifkan untuk memberikan akses premium kepada perusahaan
                    </FormHelperText>
                  </VStack>
                  <Switch
                    size="lg"
                    colorScheme="blue"
                    isChecked={formData.verified}
                    onChange={(event) => handleSwitchChange(event, "verified")}
                  />
                </Flex>
              </FormControl>

              <Divider />

              {/* Expiry Date */}
              <FormControl>
                <Flex justify="space-between" align="center" gap={4}>
                  <VStack align="start" spacing={1} flex={1}>
                    <FormLabel mb={0} fontSize="lg" fontWeight="semibold" display="flex" alignItems="center" gap={2}>
                      <Icon as={FiCalendar} color="orange.400" />
                      Tanggal Berakhir
                    </FormLabel>
                    <FormHelperText mt={0} fontSize="sm">
                      Tentukan kapan layanan premium akan berakhir
                    </FormHelperText>
                  </VStack>
                  <Box minW="200px">
                    <Datepicker
                      className="text-black rounded-lg form-control"
                      placeHolder={formatDate(company.company_expired)}
                      selectedDate={selectedExpiredDate}
                      onDateChange={handleExpiredDateChange}
                    />
                  </Box>
                </Flex>
              </FormControl>

              <Divider />

              {/* Max Employees */}
              <FormControl>
                <Flex justify="space-between" align="center" gap={4}>
                  <VStack align="start" spacing={1} flex={1}>
                    <FormLabel mb={0} fontSize="lg" fontWeight="semibold" display="flex" alignItems="center" gap={2}>
                      <Icon as={FiUsers} color="green.400" />
                      Jumlah Maksimal Anggota
                    </FormLabel>
                    <FormHelperText mt={0} fontSize="sm">
                      Batasi jumlah maksimal anggota yang dapat bergabung
                    </FormHelperText>
                  </VStack>
                  <NumberInput
                    step={5}
                    min={0}
                    minW="120px"
                    maxW="150px"
                    value={totalEmployees}
                    onChange={handleTotalEmployeesChange}
                  >
                    <NumberInputField placeholder={company.total_employee?.toString()} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Flex>
              </FormControl>

              <Divider />

              {/* Reminder Settings */}
              {/* <FormControl>
                <FormLabel fontSize="lg" fontWeight="semibold" mb={4} display="flex" alignItems="center" gap={2}>
                  <Icon as={FiInfo} color="purple.400" />
                  Pengaturan Reminder Masa Berakhir
                </FormLabel>
                <VStack spacing={4} align="stretch" bg={cardBg} p={4} borderRadius="md">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="md" fontWeight="medium">7 Hari Sebelum Berakhir</Text>
                    <Switch 
                      colorScheme="purple"
                      isChecked={formData.reminders.seven_days}
                      onChange={(e) => handleReminderChange(e, "seven_days")}
                    />
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="md" fontWeight="medium">1 Hari Sebelum Berakhir</Text>
                    <Switch 
                      colorScheme="purple"
                      isChecked={formData.reminders.one_day}
                      onChange={(e) => handleReminderChange(e, "one_day")}
                    />
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="md" fontWeight="medium">Hari-H</Text>
                    <Switch 
                      colorScheme="purple"
                      isChecked={formData.reminders.same_day}
                      onChange={(e) => handleReminderChange(e, "same_day")}
                    />
                  </Flex>
                </VStack>
              </FormControl> */}

              {/* Action Buttons */}
              <Flex justify="flex-end" gap={3} pt={4}>
                <Button
                  variant="outline"
                  size="lg"
                  borderRadius="full"
                  onClick={() => handleResetForm("update")}
                  isDisabled={!hasChanges}
                >
                  Reset
                </Button>
                <Button
                  colorScheme="blue"
                  size="lg"
                  borderRadius="full"
                  onClick={handleUpdateCompany}
                  isLoading={isUpdating}
                  loadingText="Saving..."
                  isDisabled={!hasChanges}
                >
                  Simpan Perubahan
                </Button>
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* Status Management Section */}
        <Card>
          <CardBody>
            <VStack spacing={6}>
              {/* Active/Inactive Toggle */}
              <Flex justify="space-between" align="center" w="full">
                <VStack align="start" spacing={1} flex={1}>
                  <Text fontSize="lg" fontWeight="semibold" display="flex" alignItems="center" gap={2}>
                    <Icon as={company.status === "active" ? FiToggleRight : FiToggleLeft} 
                          color={company.status === "active" ? "green.400" : "red.400"} />
                    {company.status === "active" ? "Nonaktifkan Perusahaan?" : "Aktifkan Perusahaan?"}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {company.status === "active" 
                      ? "Pengguna tidak akan dapat mengakses akun KejarTugas milik perusahaan ini" 
                      : "Pengguna akan dapat mengakses akun KejarTugas milik perusahaan ini"}
                  </Text>
                </VStack>
                <Switch
                  colorScheme="green"
                  size="lg"
                  isChecked={formData.status === "active"}
                  onChange={(event) => handleSwitchActiveChange(event, "status")}
                />
              </Flex>

              <Divider />

              {/* Delete Section */}
              <Flex justify="space-between" align="center" w="full">
                <VStack align="start" spacing={1} flex={1}>
                  <Text fontSize="lg" fontWeight="semibold" color="red.500">
                    Hapus Perusahaan
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Tindakan ini tidak dapat dibatalkan. Semua data akan terhapus permanen.
                  </Text>
                </VStack>
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="lg"
                  borderRadius="full"
                  leftIcon={<RiDeleteBin5Line />}
                  onClick={onOpenDelete}
                  _hover={{ bg: "red.50", borderColor: "red.400" }}
                >
                  Hapus Perusahaan
                </Button>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Status Change Confirmation Dialog */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={() => handleResetForm("active")}
        isOpen={isOpenActive}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {company.status === "active" ? "Nonaktifkan Perusahaan?" : "Aktifkan Perusahaan?"}
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                {company.company_name}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {company.status === "active" 
                  ? "Menonaktifkan perusahaan akan mencegah semua pengguna mengakses sistem."
                  : "Mengaktifkan perusahaan akan memberikan akses kembali kepada semua pengguna."}
              </Text>
              <Checkbox
                isChecked={confirmDelete}
                onChange={handleCheckboxChange}
                size="lg"
                colorScheme="blue"
              >
                <Text fontSize="sm">
                  {company.status === "active" 
                    ? "Saya ingin menonaktifkan perusahaan ini" 
                    : "Saya ingin mengaktifkan perusahaan ini"}
                </Text>
              </Checkbox>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              borderRadius="full"
              ref={cancelRef}
              onClick={() => handleResetForm("active")}
            >
              Batal
            </Button>
            <Button
              colorScheme="blue"
              borderRadius="full"
              ml={3}
              isDisabled={!confirmDelete}
              isLoading={isUpdating}
              onClick={handleUpdateCompany}
            >
              Konfirmasi
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onCloseDelete}
        isOpen={isOpenDelete}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.500">
            Hapus Perusahaan?
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                {company.company_name}
              </Text>
              <Box bg="red.50" p={3} borderRadius="md" border="1px solid" borderColor="red.200">
                <Text fontSize="sm" color="red.700" fontWeight="medium">
                  ⚠️ Peringatan: Tindakan ini tidak dapat dibatalkan!
                </Text>
                <Text fontSize="sm" color="red.600" mt={1}>
                  Semua data, pengguna, dan riwayat yang terkait dengan perusahaan ini akan dihapus secara permanen.
                </Text>
              </Box>
              <Checkbox
                isChecked={confirmDelete}
                onChange={handleCheckboxChange}
                size="lg"
                colorScheme="red"
              >
                <Text fontSize="sm" color="red.600">
                  Saya memahami konsekuensi dan ingin menghapus perusahaan ini
                </Text>
              </Checkbox>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              borderRadius="full"
              ref={cancelRef}
              onClick={onCloseDelete}
            >
              Batal
            </Button>
            <Button
              colorScheme="red"
              borderRadius="full"
              ml={3}
              isDisabled={!confirmDelete}
              isLoading={isUpdating}
              onClick={() => handleDeleteCompany(company.id)}
            >
              Hapus Permanen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default UserDetail;